import React, { useEffect, useRef, useState } from 'react';
import './ConfirmDialog.css';

interface ConfirmDialogProps {
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  /** Red confirm button for destructive actions */
  danger?: boolean;
  /** When set, the confirm button stays disabled until the user types this word */
  requireTypedWord?: string;
  typePrompt?: string;
  busy?: boolean;
  /** Extra controls (checkboxes, options) shown between message and buttons */
  children?: React.ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

// In-app replacement for window.confirm: themed, keyboard-friendly, and able
// to gate a destructive action behind a typed word.
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  message,
  confirmLabel,
  cancelLabel,
  danger = false,
  requireTypedWord,
  typePrompt,
  busy = false,
  children,
  onConfirm,
  onCancel,
}) => {
  const [typed, setTyped] = useState('');
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Focus lands on Cancel, not Confirm — a stray Enter shouldn't delete anything
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  // Toggling the destructive option back on starts the typed gate over
  useEffect(() => {
    setTyped('');
  }, [requireTypedWord]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) {
        e.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [busy, onCancel]);

  const typedOk =
    !requireTypedWord || typed.trim().toLowerCase() === requireTypedWord.toLowerCase();
  const canConfirm = typedOk && !busy;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canConfirm) onConfirm();
  };

  return (
    <div className="confirm-dialog-overlay" onClick={() => !busy && onCancel()}>
      <div
        className="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="confirm-dialog-title">{title}</h2>
        <div className="confirm-dialog-message">{message}</div>

        {children && <div className="confirm-dialog-extra">{children}</div>}

        <form onSubmit={handleSubmit}>
          {requireTypedWord && (
            <div className="confirm-dialog-type">
              <label htmlFor="confirm-dialog-input">{typePrompt}</label>
              <input
                id="confirm-dialog-input"
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                autoFocus
                disabled={busy}
              />
            </div>
          )}

          <div className="confirm-dialog-actions">
            <button
              type="button"
              ref={cancelRef}
              className="confirm-dialog-cancel"
              onClick={onCancel}
              disabled={busy}
            >
              {cancelLabel}
            </button>
            <button
              type="submit"
              className={`confirm-dialog-confirm ${danger ? 'danger' : ''}`}
              disabled={!canConfirm}
            >
              {confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfirmDialog;
